import React, { useEffect, useMemo, useRef } from 'react';
import P from 'prop-types';
import Carousel from '@nerdwallet/react-carousel';
import classNames from 'classnames';

import { CenteredContainer, Box, Button } from '@nerdwallet/currency';
import Right from '@nerdwallet/react-icon/gizmo/Right';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash/';

import getTextColor from '../../lib/text-color-config';

import FlexCardItem from './flex-card-item/flex-card-item';
import { CARDS_PER_ROW_ENUM, CARD_TREATMENT_ENUM } from './lib/constants';
import getEditingConfig from './lib/editing-config';

import './flex-card.less';
import UniversalContainer from '../universal-container/universal-container';
import UniversalText from '../universal-text/universal-text';
import { defaultProps } from './lib/default-options';
import { updateComponent } from '../../containers/builder/actions';

const setRowStyle = (cardsPerRow, cards, size) => {
  const total = Math.ceil(cards.length / parseInt(cardsPerRow, 10));
  if (size === 'md' && cardsPerRow === '4') {
    return {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: `repeat(${Math.ceil(cards.length / 3)}, 1fr)`,
    };
  }
  if (size === 'sm') {
    return {
      gridTemplateColumns: `repeat(1, 1fr)`,
      gridTemplateRows: `repeat(${cards.length}, 1fr)`,
    };
  }
  return {
    gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
    gridTemplateRows: `repeat(${total}, 1fr)`,
  };
};

const NextSlide = ({ onClick, arrowColor, className }) => {
  const classStyle = className.replace('slick-arrow slick-next', '');

  return (
    <Button
      className={classNames([classStyle, 'flex-card-list-carousel-arrow'])}
      tertiary
      onClick={onClick}
    >
      <Right size={30} color={arrowColor} />
    </Button>
  );
};

const FlexCardV2 = ({
  container,
  title,
  subtitle,
  arrowColor,
  stacked,
  size,
  cardsPerRow,
  desktopCarousel,
  cardsToPreview,
  cards,
  wideSizeLayout,
  carouselDotColorOptions,
  carouselInactiveDotColorOptions,
  carouselArrowColorOptions,
  carouselArrowBackgroundOptions,
  displayMobileCarouselArrow,
  arrowsOnTheEdges,
  disableDefaultProps,
  selectedComponentId,
  flatOutline,
  updateComponentProps,
}) => {
  const { textColor: carouselDotColor } = getTextColor(carouselDotColorOptions);
  const { textColor: carouselInactiveDotColor } = getTextColor(
    carouselInactiveDotColorOptions
  );
  const { textColor: carouselArrowColor } = getTextColor(
    carouselArrowColorOptions
  );
  const { textColor: carouselArrowBackgroundColor } = getTextColor(
    carouselArrowBackgroundOptions
  );
  const isMobileCarousel = size === 'sm' && !stacked;
  const isDesktopCarousel = desktopCarousel && size !== 'sm';
  const flexCardContainerRef = useRef();

  const currentComponent = flatOutline?.[selectedComponentId];
  const emptyPropsRef = useRef();

  useEffect(() => {
    if (
      currentComponent &&
      !currentComponent?.props?.title &&
      !disableDefaultProps
    ) {
      emptyPropsRef.current = currentComponent;
      updateComponentProps({ ...currentComponent, props: { ...defaultProps } });
    }
  }, [currentComponent, disableDefaultProps, updateComponentProps]);

  useEffect(() => {
    if (disableDefaultProps) {
      updateComponentProps({
        ...emptyPropsRef.current,
        props: { disableDefaultProps: true },
      });
    }
  }, [disableDefaultProps, updateComponentProps]);

  const containerProps = useMemo(
    () =>
      !disableDefaultProps
        ? {
            ...container,
            backgroundColor:
              container?.backgroundColor ||
              defaultProps.container.backgroundColor,
            hexColor: container?.hexColor || defaultProps.container.hexColor,
          }
        : container,
    [disableDefaultProps, container]
  );

  const titleProps = useMemo(
    () => ({
      ...title,
      text:
        !disableDefaultProps && !title?.text
          ? defaultProps.title.text
          : title?.text,
      groupButton:
        !disableDefaultProps && !title?.groupButton
          ? defaultProps?.title.groupButton
          : title?.groupButton,
      textSize:
        !disableDefaultProps && !title?.textSize
          ? defaultProps.title.textSize
          : title?.textSize,
      margin:
        !disableDefaultProps && !title?.margin?.currency?.bottom
          ? defaultProps.title.margin
          : title?.margin,
      padding:
        !disableDefaultProps && !title?.padding?.currency?.top
          ? defaultProps.title.padding
          : title?.padding,
    }),
    [disableDefaultProps, title]
  );

  const subtitleProps = useMemo(
    () => ({
      ...subtitle,
      text:
        !disableDefaultProps && !subtitle?.text
          ? defaultProps.subtitle.text
          : subtitle?.text,
      groupButton:
        !disableDefaultProps && !subtitle?.groupButton
          ? defaultProps?.subtitle.groupButton
          : subtitle?.groupButton,
      textSize:
        !disableDefaultProps && !subtitle?.textSize
          ? defaultProps.subtitle.textSize
          : subtitle?.textSize,
      textType:
        !disableDefaultProps && !subtitle?.textType
          ? defaultProps.subtitle.textType
          : subtitle?.textType,
    }),
    [disableDefaultProps, subtitle]
  );

  useEffect(() => {
    if (flexCardContainerRef.current) {
      const dotList =
        flexCardContainerRef.current.querySelector('ul.slick-dots');

      if (dotList && carouselDotColor) {
        dotList.classList.add('custom-dot-color');
        dotList.style.setProperty('--activeDot', carouselInactiveDotColor);
        dotList
          .querySelectorAll('.flex-card-list-dots li button')
          .forEach((button) => {
            // eslint-disable-next-line no-param-reassign
            button.style.color = carouselDotColor;
          });
      }

      const slickArrows =
        flexCardContainerRef.current.querySelectorAll('button.slick-arrow');

      if (slickArrows.length) {
        slickArrows.forEach((arrow) => {
          if (carouselArrowBackgroundColor) {
            // eslint-disable-next-line no-param-reassign
            arrow.style.backgroundColor = carouselArrowBackgroundColor;
          }
          if (carouselArrowColor) {
            // eslint-disable-next-line no-param-reassign
            arrow.style.color = carouselArrowColor;
          }
        });
      }
    }
  }, [
    carouselArrowBackgroundColor,
    carouselArrowColor,
    carouselDotColor,
    carouselInactiveDotColor,
    displayMobileCarouselArrow,
    isDesktopCarousel,
  ]);
  const rowStyle = setRowStyle(cardsPerRow, cards, size);
  const mobileStackedOrWide =
    (size !== 'sm' && !desktopCarousel) || (size === 'sm' && stacked);

  return (
    <Box ref={flexCardContainerRef}>
      <UniversalContainer {...containerProps}>
        <CenteredContainer
          className={classNames([
            'flex-card-wrapper',
            {
              carousel: (size === 'sm' && !stacked) || desktopCarousel,
              'oversize-layout': !wideSizeLayout || isDesktopCarousel,
              'no-arrow': !displayMobileCarouselArrow,
              'arrows-on-the-edges': arrowsOnTheEdges,
            },
          ])}
        >
          <UniversalText {...titleProps} />
          <UniversalText
            {...subtitleProps}
            defaultOptions={{ text: { textType: 'text' } }}
          />
          {mobileStackedOrWide && (
            <Box style={rowStyle} className="flex-card-list-container">
              {cards.map((item) => (
                <FlexCardItem key={item.$unique_key} {...item} />
              ))}
            </Box>
          )}
          {isMobileCarousel && (
            <Carousel
              id="nerd-carousel"
              autoplay={false}
              allowLooping
              decorateNav={false}
              enableDotNavigation
              dots
              nextArrow={
                displayMobileCarouselArrow ? (
                  <NextSlide arrowColor={arrowColor} />
                ) : null
              }
              dotsClass="slick-dots flex-card-list-dots"
              slidesToShow="1.15"
              className="flex-card-list-container"
            >
              {cards.map((item) => (
                <FlexCardItem key={item.$unique_key} {...item} />
              ))}
            </Carousel>
          )}
          {isDesktopCarousel && (
            <Carousel
              id="nerd-carousel"
              autoplay={false}
              allowLooping
              decorateNav={false}
              enableDotNavigation
              dots
              dotsClass="slick-dots flex-card-list-dots"
              slidesToShow={cardsToPreview || '3'}
              className="flex-card-list-container"
            >
              {cards.map((item) => (
                <FlexCardItem
                  key={item.$unique_key}
                  desktopCarousel={desktopCarousel}
                  cardsPerRow={cardsToPreview}
                  {...item}
                />
              ))}
            </Carousel>
          )}
        </CenteredContainer>
      </UniversalContainer>
    </Box>
  );
};

FlexCardV2.getEditingConfig = getEditingConfig;

FlexCardV2.displayName = 'Flex Card';

const mapDispatchToProps = (dispatch) => ({
  updateComponentProps: (componentId) => {
    dispatch(updateComponent(componentId));
  },
});

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
    state: (state) => state,
    selectedComponentId: _.property('builder.selected'), // get Editor Panel selected component
    flatOutline: _.property('builder.flatOutline'), // get Editor Panel current component data
  }),
  mapDispatchToProps
)(FlexCardV2);

NextSlide.propTypes = {
  className: P.string,
  onClick: P.func,
  arrowColor: P.string,
};

FlexCardV2.propTypes = {
  cards: P.array,
  stacked: P.bool,
  size: P.string,
  cardsPerRow: P.string,
  desktopCarousel: P.bool,
  cardTreatment: P.string,
  borderColorTreatment: P.string,
  borderSizeTreatment: P.string,
  shadowColorTreatment: P.string,
  arrowColor: P.string,
  wideSizeLayout: P.bool,
  cardsToPreview: P.string,
  displayMobileCarouselArrow: P.bool,
  arrowsOnTheEdges: P.bool,
  carouselDotColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  carouselInactiveDotColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  carouselArrowColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  carouselArrowBackgroundOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  container: P.object,
  title: P.object,
  subtitle: P.object,
  children: P.node,
  disableDefaultProps: P.bool,
  selectedComponentId: P.string,
  flatOutline: P.object,
  updateComponentProps: P.func,
};

FlexCardV2.defaultProps = {
  cards: [],
  cardsPerRow: CARDS_PER_ROW_ENUM.THREE,
  cardsToPreview: CARDS_PER_ROW_ENUM.THREE,
  stacked: false,
  cardTreatment: CARD_TREATMENT_ENUM.NONE,
  shadowColorTreatment: '#cdcdcd',
  borderColorTreatment: '#cdcdcd',
  borderSizeTreatment: '1px',
  titleSize: 'x-large',
  arrowColor: '#008255',
  displayMobileCarouselArrow: false,
  arrowsOnTheEdges: false,
  carouselDotColorOptions: {
    hexColor: '#008255',
  },
  carouselInactiveDotColorOptions: {
    hexColor: '#cdcdcd',
  },
  disableDefaultProps: false,
  children: null,
};
